import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  HStack,
  type HTMLChakraProps,
  TabsList as RawTabsList,
  TabsTrigger as RawTabsTrigger,
  type SegmentedControlOption,
  TabsRoot,
  TertiaryButton,
  chakra,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import { DsMultiSelect } from '~/components/common/DsMultiSelect'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import {
  civilDateToDate,
  civilDateToInputValue,
  parseDateInputValue,
} from '~/models/civilDate'
import { now as serverNow } from '~/models/clock'
import { formatDateDisplay, formatDateValueJst } from '~/models/dateTime'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { CASE_SECTION_TABS } from '~/pages/WorkflowCases/models/filterOptions'
import { workflowCaseSectionLabelKey } from '~/pages/WorkflowCases/models/workflowCase'
import type { WorkflowCaseStatusSegment } from '~/pages/WorkflowCases/models/workflowCaseFilter'

// chakra v3 の `TabsList*Props` は本プロジェクトの型解決 (ecloud 0.14 / chakra 3.29 の
// d.ts 解決問題) で `HTMLChakraProps` が collapse し `children` / style prop が欠落する。
// v3 本来の prop シグネチャ (`HTMLChakraProps<element>`) へ cast して復元する。
// ランタイムは ecloud のまま。DS 側が d.ts を修正したら外してよい。
const TabsList = RawTabsList as React.FC<HTMLChakraProps<'div'>>
const TabsTrigger = RawTabsTrigger as React.FC<
  HTMLChakraProps<'button'> & { value: string }
>

export type FilterBarState = {
  section: WorkflowSection | null
  /**
   * ステータスフィルタ (spec AC-05、workflow-delete-transition design §4):
   * segment が基底集合を決め、showComplete/showDeleted が加算される
   * (`deriveWorkflowCaseStatuses` で repeated statuses に変換する)。
   */
  statusSegment: WorkflowCaseStatusSegment
  showComplete: boolean
  showDeleted: boolean
  /**
   * workflow-list-action-columns AC-07/AC-08、spec §機能詳細のフィルタ
   * (workflow.md §415): 「あなたが対応すべきもののみ」 (既定 OFF)。
   * server-side filter で、他のフィルタとは全て AND で合成される。
   * ステータス checkbox との連動は入れない (完了ケースは導出上自然に対象外
   * になるため)。
   */
  onlyViewerActionRequired: boolean
  /** workflow-fix-list AC-04/AC-06/AC-07: エリアの chip 式マルチ選択 (server-side filter) */
  areaCodes: AreaCode[]
  /** workflow-fix-list AC-05/AC-06/AC-07: BG の chip 式マルチ選択 (backend request field が無いため client-side filter) */
  bgCodes: string[]
  /**
   * AC-09: タグ label のチップ式マルチ選択 (OR 条件)。 backend の tag_labels フィルタは
   * 実装済みだが、 bgCode と同じ理由 (候補一覧を取得済み rows から導出するため
   * サーバー往復を追加しない) で client-side filter として扱う (index.tsx)。
   */
  tagLabels: string[]
  /**
   * AC-WFW-001 機能詳細: 対象日期間フィルタ (From ～ To の日付ピッカー + 当日/翌日
   * クイック絞り込みチップ)。 空文字 = 未指定 (フィルタなし)。
   */
  targetDateFrom: string
  targetDateTo: string
}

// spec AC-05: 既定 = セグメント「すべて」+ 両 checkbox OFF (= [NOT_STARTED, IN_PROGRESS] のみ表示)。
// workflow-fix-list: areaCodes/bgCodes は空配列 = フィルタなし (設計書 §5)。
export const DEFAULT_FILTER_STATE: FilterBarState = {
  section: null,
  statusSegment: 'all',
  showComplete: false,
  showDeleted: false,
  onlyViewerActionRequired: false,
  areaCodes: [],
  bgCodes: [],
  tagLabels: [],
  targetDateFrom: '',
  targetDateTo: '',
}

const ALL_TAB_VALUE = ''

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
const WORKFLOW_CASES_FILTER_STORAGE_KEY = 'workflowCases'

type Props = {
  filter: FilterBarState
  onChange: (next: FilterBarState) => void
  /** spec AC-WFW-001: 対象コード候補 (BG + 発電の計画提出者、エリア連動なし) */
  bgOptions: ReadonlyArray<{ code: string; name: string }>
  /** AC-09: タグフィルタの候補元 (取得済み rows から導出した重複無し label 一覧) */
  tagOptions: string[]
}

export const FilterBar = ({
  filter,
  onChange,
  bgOptions,
  tagOptions,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // 件数 = 「クリア」で戻る条件のうち既定から変えているもの (計画断面のタブはカードの外なので数えない)
  const isTargetDateSet =
    filter.targetDateFrom !== '' || filter.targetDateTo !== ''
  const modifiedCount = [
    filter.statusSegment !== 'all',
    filter.showComplete,
    filter.showDeleted,
    filter.onlyViewerActionRequired,
    filter.areaCodes.length > 0,
    filter.bgCodes.length > 0,
    filter.tagLabels.length > 0,
    isTargetDateSet,
  ].filter(Boolean).length

  // 要約の日付は日付欄と同じ `YYYY/MM/DD (曜)`
  const summaryDate = (value: string): string => {
    const civil = parseDateInputValue(value)
    if (civil === undefined) return ''
    const date = civilDateToDate(civil)
    return t('common:date.withWeekday', {
      date: formatDateDisplay(date),
      weekday: t(`common:date.calendar.weekday.${WEEKDAY_KEYS[date.getDay()]}`),
    })
  }
  const all = t('common:filter.all')
  const joinValues = (values: string[]): string =>
    values.length === 0 ? all : values.join('・')
  // チェックボックスの条件は ON のものをステータスの値の後ろに添える (「進行中 (完了を表示)」)
  const checkedLabels = [
    filter.showComplete && t('page:workflowCases.filter.showComplete'),
    filter.showDeleted && t('page:workflowCases.filter.showDeleted'),
    filter.onlyViewerActionRequired &&
      t('page:workflowCases.filter.onlyViewerActionRequired'),
  ].filter((v): v is string => v !== false)
  const segmentLabel =
    filter.statusSegment === 'notStarted'
      ? t('page:workflowCases.filter.statusSegmentNotStarted')
      : filter.statusSegment === 'inProgress'
        ? t('page:workflowCases.filter.statusSegmentInProgress')
        : all
  const summary: ToolbarStatusItem[] = [
    {
      key: 'area',
      label: t('page:workflowCases.filter.area'),
      value: joinValues(filter.areaCodes.map(convertAreaLabel)),
    },
    {
      key: 'targetDate',
      label: t('page:workflowCases.filter.targetDate'),
      value: isTargetDateSet
        ? `${summaryDate(filter.targetDateFrom)}〜${summaryDate(filter.targetDateTo)}`
        : all,
    },
    {
      key: 'status',
      label: t('page:workflowCases.filter.status'),
      value:
        checkedLabels.length === 0
          ? segmentLabel
          : `${segmentLabel} (${checkedLabels.join('・')})`,
    },
    {
      key: 'bg',
      label: t('page:workflowCases.filter.bg'),
      value: joinValues(filter.bgCodes),
    },
    {
      key: 'tags',
      label: t('page:workflowCases.filter.tags'),
      value: joinValues(
        filter.tagLabels.map((label) => localizeBuiltinWorkflowText(label, t)),
      ),
    },
  ]

  // AC-WFW-001 機能詳細: 当日/翌日 のクイック絞り込みチップは From=To=該当日付をセット。
  // クリック時に new Date() を評価し、日付をまたいだ後も「当日 = 本日」になるよう固定しない。
  // 業務日付は JST 基準（mock-app Cases.razor の SlotTimeHelper.NowJst() 準拠）。
  const getTodayIso = (): string => formatDateValueJst()
  const getTomorrowIso = (): string =>
    formatDateValueJst(new Date(serverNow().getTime() + 24 * 60 * 60 * 1000))

  /**
   * 当日 / 翌日 は **set アクション**で、 押下状態 (selected) を持たない。
   *
   * DS に「選択解除できる弱い選択表現」 が無く、 chakra の solid/blue を当てると
   * DS のアクセント色とも 8080 とも違う青になる (デザイナー指摘 2026-08-12)。
   * スポット入札の同型 UI も 2026-06-10 に同じ理由で persistent active を撤去済み
   * (#5148 で 2026-08-12 に「現状維持」 として再確認)。
   *
   * 選択状態を持たないので**再押下での解除も無い** (解除は右のカレンダーの
   * 「選択をクリア」 か フィルタ解除で行う)。 押した結果は右のカレンダーの
   * 表示に出るので、 フィードバックはそこが担う。
   */
  const setTargetDateQuick = (range: 'today' | 'tomorrow'): void => {
    const target = range === 'today' ? getTodayIso() : getTomorrowIso()
    onChange({ ...filter, targetDateFrom: target, targetDateTo: target })
  }

  // ---------------------------------------------------------------------------
  // 対象日は **開始日 / 終了日の 2 入力**で持つ。
  //
  // 一度 ecloud `DateRangePicker` (受給期間と同じ部品) に寄せたが、 DS の期間カレンダーは
  // 「1 クリック目 = 開始日 / 2 クリック目 = 終了日」 しか作れず **「終了日のみ指定」 が
  // UI から到達不能**になっていた。 spec `workflow.md` AC-WFW-001 機能詳細 (P0) は
  // 「From のみ指定: From 以降を全て表示 / To のみ指定: To 以前を全て表示」 を要求しており、
  // backend (`workflow_case_repository.go` が From/To を独立に適用) / proto (`optional`) /
  // request 組み立ても片側指定に対応済みで、 **UI だけが作れない**状態だった。
  //
  // デザイナー判断 (2026-08-12) により 2 入力へ戻す。 「受給期間と同じ部品にする」 という
  // 先の指示より **片側指定できること (spec P0)** を優先する。
  // ---------------------------------------------------------------------------

  // AC-05: セグメント (すべて/未着手/進行中) の選択肢。
  // design (design/demand/cases.html の `.ecl-segmented`) が SegmentedControl
  // (帯の中の白セグメント) で描いているため DS `SegmentedControl` を使う。
  // [規約差異] 択一グループは ToggleButton が既定だが、 ToggleButton (solid/blue + outline) は design と別部品の見た目になり、
  // 非選択の枠 3px / 文字 12px も DS トークン外の chakra 既定値で描画されていた
  // (2026-07-30 実測)。 design を UI の正本とする `design/CLAUDE.md` に従い design 側へ寄せる。
  // フィルタ用途での SegmentedControl 採用可否は issue #3381 (Des確認) の論点でもあるため、
  // 規約と #3381 の確定内容に応じて再度寄せ先を見直す。
  const statusSegmentOptions: Array<
    SegmentedControlOption<FilterBarState['statusSegment']>
  > = [
    { value: 'all', label: t('page:workflowCases.filter.all') },
    {
      value: 'notStarted',
      label: t('page:workflowCases.filter.statusSegmentNotStarted'),
    },
    {
      value: 'inProgress',
      label: t('page:workflowCases.filter.statusSegmentInProgress'),
    },
  ]

  const sectionTabItems: ReadonlyArray<{
    key: string
    label: React.ReactNode
  }> = [
    { key: ALL_TAB_VALUE, label: t('page:workflowCases.filter.all') },
    ...CASE_SECTION_TABS.map((section) => ({
      key: String(section),
      label: t(workflowCaseSectionLabelKey(section) ?? ''),
    })),
  ]

  return (
    <>
      {/* 計画断面フィルタ: design (design/demand/cases.html:156-161) の `.ecl-tabs` を
          再現する raw slot tabs に統一 (旧 SegmentedControl から差し替え)。design は
          タブ行をフィルタ群と独立させた行にしているため、ここでも
          FilterToolbar (ステータス/エリア/BG) とは別行で描画する。 */}
      <TabsRoot
        onValueChange={(details: { value: string }) => {
          const key = details.value
          onChange({
            ...filter,
            section:
              key === ALL_TAB_VALUE ? null : (Number(key) as WorkflowSection),
          })
        }}
        value={filter.section === null ? ALL_TAB_VALUE : String(filter.section)}
      >
        {/* paddingBottom 1px は選択タブの下線を削らせないための実寸補正。
            DS の選択タブは `border-bottom 2px` + `marginBottom -1px` で list の
            境界線に重ねる作りなので、 1px が list の外へはみ出す。 overflowY: hidden
            (= 横スクロール時の縦スクロールバー抑止) と併せると、そのはみ出した 1px が
            padding box で clip されて下線が 1px に見える (2026-07-30 dev 実測)。
            padding で 1px 分の逃げを作ると 2px 全体が描画される。 */}
        <TabsList
          overflowX="auto"
          overflowY="hidden"
          paddingBottom="1px"
          aria-label={t('page:workflowCases.filter.section')}
        >
          {sectionTabItems.map((item) => (
            <TabsTrigger
              key={item.key}
              fontSize={tabTriggerFontSize.token}
              value={item.key}
            >
              <span>{item.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </TabsRoot>

      <CollapsibleFilterToolbar
        modifiedCount={modifiedCount}
        // 計画断面のタブはカードの外なので数えず、 クリアでも戻さない (件数と戻す範囲をそろえる)
        onClear={() => {
          onChange({ ...DEFAULT_FILTER_STATE, section: filter.section })
        }}
        storageKey={WORKFLOW_CASES_FILTER_STORAGE_KEY}
        summary={summary}
      >
        <FilterGrid
          items={[
            {
              key: 'area',
              label: t('page:workflowCases.filter.area'),
              // workflow-fix-list AC-04/AC-06/AC-07: エリアの chip 式マルチ選択
              // (server-side filter、タグフィルタと同じ DS `Select` の multi = `DsMultiSelect`)。
              // 幅は 3 つとも固定で、 chip が増えたら DS の既定どおり折り返して縦に伸びる
              // (rules/frontend.md「Select の幅は選択の前後で変えない」、 issue #4496 で
              // 自前 `MultiSelect` の `sizing="fit"` から移行)。
              control: (
                <Box flexShrink={0} maxWidth="100%">
                  <DsMultiSelect
                    width="240px"
                    aria-label={t('page:workflowCases.filter.area')}
                    onChange={(next) => {
                      onChange({
                        ...filter,
                        areaCodes: [...next].map((v) => Number(v) as AreaCode),
                      })
                    }}
                    options={AREA_CODE_OPTIONS.map((code) => ({
                      value: String(code),
                      label: convertAreaLabel(code),
                    }))}
                    placeholder={t('page:workflowCases.filter.allAreas')}
                    selected={new Set(filter.areaCodes.map(String))}
                  />
                </Box>
              ),
            },
            {
              // AC-WFW-001 機能詳細: 対象日期間フィルタ (期間ピッカー + 当日/翌日 クイック絞り込み)。
              key: 'targetDate',
              label: t('page:workflowCases.filter.targetDate'),
              control: (
                <HStack alignItems="center" flexWrap="wrap" gap={2}>
                  <ButtonTooltip
                    content={t(
                      'page:workflowCases.filter.targetDateQuickOnly',
                      {
                        date: getTodayIso(),
                      },
                    )}
                  >
                    <TertiaryButton
                      aria-label={t(
                        'page:workflowCases.filter.targetDateQuickToday',
                      )}
                      onClick={() => {
                        setTargetDateQuick('today')
                      }}
                      size="md"
                      title={t(
                        'page:workflowCases.filter.targetDateQuickOnly',
                        {
                          date: getTodayIso(),
                        },
                      )}
                    >
                      {t('page:workflowCases.filter.targetDateQuickToday')}
                    </TertiaryButton>
                  </ButtonTooltip>
                  <ButtonTooltip
                    content={t(
                      'page:workflowCases.filter.targetDateQuickOnly',
                      {
                        date: getTomorrowIso(),
                      },
                    )}
                  >
                    <TertiaryButton
                      aria-label={t(
                        'page:workflowCases.filter.targetDateQuickTomorrow',
                      )}
                      onClick={() => {
                        setTargetDateQuick('tomorrow')
                      }}
                      size="md"
                      title={t(
                        'page:workflowCases.filter.targetDateQuickOnly',
                        {
                          date: getTomorrowIso(),
                        },
                      )}
                    >
                      {t('page:workflowCases.filter.targetDateQuickTomorrow')}
                    </TertiaryButton>
                  </ButtonTooltip>
                  {/* 開始日 / 終了日を独立に指定できる 2 入力 (片側だけの指定も可)。
                spec AC-WFW-001 機能詳細の「From のみ / To のみ」 を満たすため。 

                幅は共通の日付欄の幅 (`COMPACT_DATE_FIELD_WIDTH`、 曜日付きの値と
                × ボタンが収まる幅)。 元の 176px は placeholder が 150px で見切れた
                (2026-08-12 デザイナー指摘) ための値だった。 */}
                  <Box width={COMPACT_DATE_FIELD_WIDTH}>
                    <chakra.label
                      css={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                        clipPath: 'inset(50%)',
                        whiteSpace: 'nowrap',
                      }}
                      htmlFor="workflow-cases-target-date-from"
                    >
                      {t('page:workflowCases.filter.targetDateFrom')}
                    </chakra.label>
                    <CompactCivilDateField
                      hasClear
                      id="workflow-cases-target-date-from"
                      onChange={(d) => {
                        onChange({
                          ...filter,
                          targetDateFrom: d ? civilDateToInputValue(d) : '',
                        })
                      }}
                      value={parseDateInputValue(filter.targetDateFrom)}
                    />
                  </Box>
                  <span>
                    {t('page:workflowCases.filter.targetDateRangeSeparator')}
                  </span>
                  <Box width={COMPACT_DATE_FIELD_WIDTH}>
                    <chakra.label
                      css={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden',
                        clipPath: 'inset(50%)',
                        whiteSpace: 'nowrap',
                      }}
                      htmlFor="workflow-cases-target-date-to"
                    >
                      {t('page:workflowCases.filter.targetDateTo')}
                    </chakra.label>
                    <CompactCivilDateField
                      hasClear
                      id="workflow-cases-target-date-to"
                      onChange={(d) => {
                        onChange({
                          ...filter,
                          targetDateTo: d ? civilDateToInputValue(d) : '',
                        })
                      }}
                      value={parseDateInputValue(filter.targetDateTo)}
                    />
                  </Box>
                </HStack>
              ),
            },
            {
              // AC-05: セグメント (すべて/未着手/進行中) が基底集合を決め、下の checkbox 2 個
              // (完了を表示/削除を表示) がセグメントと独立に加算される。
              key: 'status',
              label: t('page:workflowCases.filter.status'),
              control: (
                <AccessibleSegmentedControl
                  aria-label={t('page:workflowCases.filter.status')}
                  name="workflow-cases-status"
                  onSelect={(value) => {
                    onChange({ ...filter, statusSegment: value })
                  }}
                  options={statusSegmentOptions}
                  selected={filter.statusSegment}
                />
              ),
            },
            {
              key: 'showComplete',
              control: (
                <Checkbox
                  checked={filter.showComplete}
                  onCheckedChange={(details: {
                    checked: boolean | 'indeterminate'
                  }) => {
                    onChange({
                      ...filter,
                      showComplete: details.checked === true,
                    })
                  }}
                >
                  {t('page:workflowCases.filter.showComplete')}
                </Checkbox>
              ),
            },
            {
              key: 'showDeleted',
              control: (
                <Checkbox
                  checked={filter.showDeleted}
                  onCheckedChange={(details: {
                    checked: boolean | 'indeterminate'
                  }) => {
                    onChange({
                      ...filter,
                      showDeleted: details.checked === true,
                    })
                  }}
                >
                  {t('page:workflowCases.filter.showDeleted')}
                </Checkbox>
              ),
            },
            {
              // workflow-fix-list AC-05/AC-06/AC-07: BG の chip 式マルチ選択
              // (backend request field が無いため index.tsx で client-side filter)。
              key: 'bg',
              label: t('page:workflowCases.filter.bg'),
              control: (
                <Box flexShrink={0} maxWidth="100%">
                  <DsMultiSelect
                    width="320px"
                    aria-label={t('page:workflowCases.filter.bg')}
                    onChange={(next) => {
                      onChange({ ...filter, bgCodes: [...next] })
                    }}
                    options={bgOptions.map((bg) => ({
                      value: bg.code,
                      label: `${bg.code} - ${bg.name}`,
                    }))}
                    placeholder={t('page:workflowCases.filter.allBgs')}
                    selected={new Set(filter.bgCodes)}
                  />
                </Box>
              ),
            },
            {
              // AC-09: タグのチップ式マルチ選択 (プレーンテキストの chip。色付けしない)。
              key: 'tags',
              label: t('page:workflowCases.filter.tags'),
              control: (
                <Box flexShrink={0} maxWidth="100%">
                  <DsMultiSelect
                    width="240px"
                    aria-label={t('page:workflowCases.filter.tags')}
                    noOptionsMessage={t('page:workflowCases.filter.tagsEmpty')}
                    onChange={(next) => {
                      onChange({ ...filter, tagLabels: [...next] })
                    }}
                    options={tagOptions.map((label) => ({
                      value: label,
                      label: localizeBuiltinWorkflowText(label, t),
                    }))}
                    placeholder={t('page:workflowCases.filter.allTags')}
                    selected={new Set(filter.tagLabels)}
                  />
                </Box>
              ),
            },
            {
              // workflow-list-action-columns AC-07/AC-08: 「あなたが対応すべきもののみ」(server-side filter、既定 OFF)。
              key: 'onlyViewerActionRequired',
              control: (
                <Checkbox
                  checked={filter.onlyViewerActionRequired}
                  onCheckedChange={(details: {
                    checked: boolean | 'indeterminate'
                  }) => {
                    onChange({
                      ...filter,
                      onlyViewerActionRequired: details.checked === true,
                    })
                  }}
                >
                  {t('page:workflowCases.filter.onlyViewerActionRequired')}
                </Checkbox>
              ),
            },
          ]}
        />
      </CollapsibleFilterToolbar>
    </>
  )
}
